import React, { useState } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
type Answer = string | null;
export function Quiz() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState<{
    location: Answer;
    age: Answer;
    motivation: Answer;
  }>({
    location: null,
    age: null,
    motivation: null
  });
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const getHomeUrl = () => {
    const cid = localStorage.getItem('bemobclick');
    return cid ? `/?cid=${cid}` : '/';
  };
  const handleLocationAnswer = (answer: string) => {
    if (answer === 'outside') {
      setErrorMessage('Sorry, this service is currently available only for U.S. residents.');
      setShowError(true);
    } else {
      setAnswers({
        ...answers,
        location: answer
      });
      setCurrentQuestion(2);
      setShowError(false);
    }
  };
  const handleAgeAnswer = (answer: string) => {
    if (answer === 'under18') {
      setErrorMessage('You must be at least 18 years old to check your credit score.');
      setShowError(true);
    } else {
      setAnswers({
        ...answers,
        age: answer
      });
      setCurrentQuestion(3);
      setShowError(false);
    }
  };
  const handleMotivationAnswer = (answer: string) => {
    setAnswers({
      ...answers,
      motivation: answer
    });
    // Redirect to opt-in page
    navigate('/opt-in');
  };
  return <div className="w-full min-h-screen font-['Open_Sans'] bg-gradient-to-br from-[#0039a6] to-[#6a11cb]" data-id="element-252">
      <style data-id="element-253">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;600&display=swap');
          .quiz-button {
            transition: all 0.3s ease;
          }
          .quiz-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
      <div className="px-4 py-12" data-id="element-254">
        {/* Logo */}
        <div className="text-center mb-12" data-id="element-255">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TqMEYVHuTPuBcgwmU1HgU/FINANCE_TIME_%282%29.png" alt="Finance-Time" className="h-8 md:h-8 mx-auto" data-id="element-256" />
        </div>
        <div className="max-w-2xl mx-auto" data-id="element-257">
          {/* Progress indicator */}
          <div className="mb-8" data-id="element-258">
            <div className="flex justify-center gap-2" data-id="element-259">
              <div className={`h-2 w-16 rounded-full ${currentQuestion >= 1 ? 'bg-[#00C851]' : 'bg-white/30'}`} data-id="element-260" />
              <div className={`h-2 w-16 rounded-full ${currentQuestion >= 2 ? 'bg-[#00C851]' : 'bg-white/30'}`} data-id="element-261" />
              <div className={`h-2 w-16 rounded-full ${currentQuestion >= 3 ? 'bg-[#00C851]' : 'bg-white/30'}`} data-id="element-262" />
            </div>
            <p className="text-white/80 text-center mt-3 text-sm" data-id="element-263">
              Question {currentQuestion} of 3
            </p>
          </div>
          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-264">
            {showError ? <div className="text-center" data-id="element-265">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-266">
                  <span className="text-3xl" data-id="element-267">⚠️</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-[#2E2E2E] mb-4" data-id="element-268">
                  Unable to Continue
                </h2>
                <p className="text-lg text-gray-600 mb-8" data-id="element-269">{errorMessage}</p>
                <a href={getHomeUrl()} className="inline-block bg-[#0039a6] text-white text-lg font-semibold px-8 py-4 rounded-xl hover:bg-[#002d80] transition-colors" data-id="element-270">
                  Return to Home
                </a>
              </div> : <>
                {/* Question 1: Location */}
                {currentQuestion === 1 && <div data-id="element-271">
                    <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-272">
                      Where do you currently live?
                    </h2>
                    <div className="space-y-4" data-id="element-273">
                      <button onClick={() => handleLocationAnswer('us')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-4" data-id="element-274">
                        <img src="https://flagcdn.com/w40/us.png" alt="USA Flag" className="w-8 h-6" data-id="element-275" />
                        <span data-id="element-276">United States</span>
                      </button>
                      <button onClick={() => handleLocationAnswer('outside')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-4" data-id="element-277">
                        <span className="text-3xl" data-id="element-278">🌎</span>
                        <span data-id="element-279">Outside the U.S.</span>
                      </button>
                    </div>
                  </div>}
                {/* Question 2: Age */}
                {currentQuestion === 2 && <div data-id="element-280">
                    <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-281">
                      How old are you?
                    </h2>
                    <div className="space-y-4" data-id="element-282">
                      <button onClick={() => handleAgeAnswer('under18')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E]" data-id="element-283">
                        Under 18
                      </button>
                      <button onClick={() => handleAgeAnswer('18-34')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E]" data-id="element-284">
                        18 - 34
                      </button>
                      <button onClick={() => handleAgeAnswer('35-54')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E]" data-id="element-285">
                        35 - 54
                      </button>
                      <button onClick={() => handleAgeAnswer('55+')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E]" data-id="element-286">
                        55+
                      </button>
                    </div>
                  </div>}
                {/* Question 3: Motivation */}
                {currentQuestion === 3 && <div data-id="element-287">
                    <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-288">
                      What's your main reason for checking your credit score
                      today?
                    </h2>
                    <div className="space-y-4" data-id="element-289">
                      <button onClick={() => handleMotivationAnswer('improve')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-3" data-id="element-290">
                        <CheckCircleIcon className="w-6 h-6 text-[#00C851]" data-id="element-291" />
                        <span data-id="element-292">I want to improve my credit</span>
                      </button>
                      <button onClick={() => handleMotivationAnswer('buying')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-3" data-id="element-293">
                        <CheckCircleIcon className="w-6 h-6 text-[#00C851]" data-id="element-294" />
                        <span data-id="element-295">I'm planning to buy a car or home</span>
                      </button>
                      <button onClick={() => handleMotivationAnswer('denied')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-3" data-id="element-296">
                        <CheckCircleIcon className="w-6 h-6 text-[#00C851]" data-id="element-297" />
                        <span data-id="element-298">I was denied for credit recently</span>
                      </button>
                      <button onClick={() => handleMotivationAnswer('curious')} className="quiz-button w-full bg-white border-2 border-gray-200 hover:border-[#00C851] text-left px-6 py-5 rounded-xl text-lg font-semibold text-[#2E2E2E] flex items-center gap-3" data-id="element-299">
                        <CheckCircleIcon className="w-6 h-6 text-[#00C851]" data-id="element-300" />
                        <span data-id="element-301">Just curious</span>
                      </button>
                    </div>
                  </div>}
              </>}
          </div>
        </div>
      </div>
    </div>;
}